import { immerable } from "immer"
import { useState } from 'react'
import api from '@/services/api/clientRelation.js';
import useComputed from "../use-computed/index";
import rem from "@/utils/rem";
import { useSelector } from 'react-redux';
import MultiLevelDropDown from "@/pages/onboarding/components/MultiLevelMenuDropDown/index";
import { renderCPStatus } from "@/utils/onboarding-status-handler";
import titleCase from "@/utils/title-case";
import { statusIdMapper } from "@/pages/onboarding/helpers/index";
class Client {
	[immerable] = true
	constructor(params) {
		this.serviceProviderOrgId = params.serviceProviderOrgId;
		this.dataOwnerOrgId = params.dataOwnerOrgId;
		this.dataElementId = params.dataElementId;
		this.dataElementName = params.dataElementName;
		this.dataOwnerEmail = params.dataOwnerEmail;
		this.serviceProviderRelationshipStatus = titleCase(params.serviceProviderRelationshipStatus);
		this.serviceProviderRelationshipId = params.serviceProviderRelationshipId;
		this.serviceProviderRelationshipInvitesId = params.serviceProviderRelationshipInvitesId;
		this.initiater = params.initiater;
		this.created = params.created;
		this.modified = params.modified;
		this.flow = params.flow;
		this.clientOrgId = params.clientOrgId;
		this.clientOrgName = params.clientOrgName;
		this.dataOwnerOrgName = params.dataOwnerOrgName;
		this.ownSystems = params.ownSystems;
		this.status = params.initiater ==='DATA_OWNER' && params.dataOwnerOrgId !==params.orgId && params.serviceProviderRelationshipStatus?.toUpperCase() ==='PENDING' ? 'PENDING YOUR APPROVAL': params.serviceProviderRelationshipStatus?.toUpperCase();
		this.statusCode = statusIdMapper[this.status?.toUpperCase()];
	}
}

const useClientRelationship = (handleAction) => {

	//  CONSTANTS
	const statusEnum = {
		APPROVED: "APPROVED",
		PENDING: "PENDING",
		REJECTED: "REJECTED",
		CANCELLED: "CANCELLED",
		REVOKED: "REVOKED",
	}

	//  STATES
	const [clientData, setClientData] = useComputed([]);
	const [clientError, setClientError] = useState();
	const [loading, setLoading] = useState(false);

	const profile = useSelector(state => state.auth.profile)

	function serialize(params) {
		return {
			serviceProviderOrgId: params.serviceProviderOrgId,
			dataOwnerOrgId: params.dataOwnerOrgId,
			dataElementId: params.dataElementId,
			dataElementName: params.dataElementName,
			dataOwnerEmail: params.dataOwnerEmail,
			serviceProviderRelationshipStatus: titleCase(params.serviceProviderRelationshipStatus),
			serviceProviderRelationshipId: params.serviceProviderRelationshipId,
			serviceProviderRelationshipInvitesId: params.serviceProviderRelationshipInvitesId,
			initiater: params.initiater,
			created: params.created,
			modified: params.modified,
			flow: params.flow,
			clientOrgId: params.clientOrgId,
			clientOrgName: params.clientOrgName,
			dataOwnerOrgName: params.dataOwnerOrgName,
			ownSystems: params.ownSystems,
			orgId : params.orgId
		}
	}

	// Fetch client data
	const fetchClientData = async (params, orgId) => {
		setLoading(true);
		const [error, response] = await api.index(params);
		setLoading(false);
		if (error) {
			return setClientError('Client Data API has error')
		}
		const serializedData = response.map(record => ({...record, orgId})).map(entry => serialize(entry))
		const newItems = serializedData.map(params => new Client(params))
		setClientData(newItems);
		return newItems;
	}

	const renderCPOrgName = (_orgName, rowData) => {
		const { dataOwnerOrgName, dataOwnerOrgId, dataOwnerEmail } = rowData;
		if (dataOwnerOrgId === profile?.organizationId) {
			return 'Myself';
		}

		return (
			dataOwnerOrgName ?? dataOwnerEmail
		);
	}

	const renderCPAction = (_action, rowData) => {
		const menuList = [];
		let hasNotifyDot = false;
		// ACTION based on statusEnum
		let { serviceProviderRelationshipStatus, serviceProviderOrgId, dataOwnerOrgId, initiater } = rowData
		serviceProviderRelationshipStatus = serviceProviderRelationshipStatus?.toUpperCase();
		if (serviceProviderRelationshipStatus === "APPROVED") {
			menuList.push({
				label: "Revoke",
				onClick: (e) => {
					e.stopPropagation();
					handleAction(rowData, "REVOKED");
				}
			})
		}
		else if (serviceProviderRelationshipStatus === "PENDING") {
			if ((initiater === "SERVICE_PROVIDER" && profile?.organizationId === serviceProviderOrgId) || (initiater === "DATA_OWNER" && profile?.organizationId === dataOwnerOrgId)) {
				menuList.push({
					label: "Cancel Request",
					onClick: (e) => {
						e.stopPropagation();
						handleAction(rowData, "CANCELLED");
					}
				})
			}
			else {
				menuList.push({
					label: "Approve",
					onClick: (e) => {
						e.stopPropagation();
						handleAction(rowData, "APPROVED");
					}
				},
					{
						label: "Reject",
						onClick: (e) => {
							e.stopPropagation();
							handleAction(rowData, "REJECTED");
						}
					}
				)
				hasNotifyDot = true;
			}
		}

		return (
			menuList?.length > 0 && <div className='on-boarding__action-items'>
				<MultiLevelDropDown
					menuList={menuList}
					hasNotifyDot={hasNotifyDot}
					id="my-client-action-menu"
				/>
			</div>
		);
	}

	const renderFlowStatus = (flow) => {
		switch (flow) {
			case "SEND":
				return "Provide on their behalf";
			case "RECEIVE":
				return "Consume on their behalf";
			default:
				return "Provide on their behalf";
		}
	}

	const clientHeader = [
		{
			key: "clientOrgName",
			label: "Client",
			width: rem(180),
			render: (orgName, rowData) => renderCPOrgName(orgName, rowData),
		},
		{
			key: 'dataElementName',
			label: 'Data Element',
			width: rem(180)
		},
		{
			key: 'flow',
			label: "Provide/Consume",
			width: rem(180),
			render: (flow) => renderFlowStatus(flow)
		},
		{
			key: 'modified',
			label: 'Modified',
			width: rem(180),
			render: (modified) => new Date(modified).toLocaleDateString(),
		},
		{
			key: "serviceProviderRelationshipStatus",
			label: "Status",
			width: rem(180),
			render: (status, rowData) => renderCPStatus(status?.toUpperCase(), rowData, profile),
		},
		{
			key: "action",
			width: rem(96),
			sortable: false,
			render: (action, rowData) => renderCPAction(action, rowData)
		},
	]

	return clientData ? {
		fetchClientData,
		renderCPOrgName,
		renderCPAction,
		renderFlowStatus,
		statusEnum,
		clientData,
		clientHeader,
		clientError,
		loading
	} : {}

}

export default useClientRelationship;

